import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { CameraIcon, VideoIcon, SearchCheckIcon, ChevronRightIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useParcelsAvailableForPhotoVideo } from '@/entities/parcels';
import { usePricingConfig } from '@/entities/pricing';
import { formatUsd } from '@/shared/utils/currency';
import type { PricingConfig } from '@/shared/types/api/pricing';

type ReportType = 'PHOTO' | 'VIDEO' | 'PROVERKA';

type Props = {
  reportType: ReportType;
};

const TITLE_KEY: Record<ReportType, string> = {
  PHOTO: 'photoTitle',
  VIDEO: 'videoTitle',
  PROVERKA: 'proverkaTitle',
};

const FEE_KEY: Record<ReportType, string> = {
  PHOTO: 'photoFee',
  VIDEO: 'videoFee',
  PROVERKA: 'proverkaFee',
};

const FEE_FIELD: Record<ReportType, keyof PricingConfig> = {
  PHOTO: 'photo_report_fee',
  VIDEO: 'video_report_fee',
  PROVERKA: 'proverka_fee',
};

const BASE_PATH: Record<ReportType, string> = {
  PHOTO: '/services/photo',
  VIDEO: '/services/video',
  PROVERKA: '/services/proverka',
};

export const WidgetsServicesReportListMain = ({ reportType }: Props) => {
  const { t } = useTranslation('services');
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } =
    useParcelsAvailableForPhotoVideo(reportType);
  const parcels = data ?? [];

  const { data: pricing } = usePricingConfig();
  const fee = pricing?.[FEE_FIELD[reportType]];

  const EmptyIcon =
    reportType === 'PHOTO' ? CameraIcon : reportType === 'VIDEO' ? VideoIcon : SearchCheckIcon;

  return (
    <>
      <PageHeader title={t(TITLE_KEY[reportType])} showBack backTo="/settings/services" />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {fee != null && (
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
            <span className="text-sm font-medium text-ink-muted">{t(FEE_KEY[reportType])}</span>
            <span className="text-base font-semibold text-ink">{formatUsd(Number(fee))}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState message={extractErrorMessage(error)} onRetry={() => void refetch()} />
        ) : parcels.length === 0 ? (
          <EmptyState
            icon={<EmptyIcon className="h-10 w-10" aria-hidden />}
            title={t('noParcelsAvailable')}
          />
        ) : (
          <>
            <p className="text-sm font-medium text-ink-muted">{t('selectParcel')}</p>
            {parcels.map((parcel) => (
              <button
                key={parcel.id}
                type="button"
                onClick={() => navigate(`${BASE_PATH[reportType]}/${parcel.id}`)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-ink">{parcel.name}</p>
                  <p className="truncate text-xs text-ink-muted">{parcel.trek_id}</p>
                  <span className="mt-1.5 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                    {t(`parcelStatus:${parcel.status}`)}
                  </span>
                </div>
                <ChevronRightIcon className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </>
  );
};
